function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const refreshIntervals = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annual",
  "lifetime",
  "manual",
] as const;

export const budgetReportFrequencies = ["weekly", "monthly", "annual"] as const;

export const budgetRefreshIntervals = refreshIntervals.filter(
  (int) => int !== "lifetime" && int !== "manual",
);

const refreshIntervalDescriptions: Record<
  (typeof refreshIntervals)[number],
  string
> = {
  annual: "Limit resets every calendar year",
  daily: "Limit resets every day at midnight UTC",
  lifetime: "Limit is fixed",
  manual: "Limit can be manually reset by an admin",
  monthly: "Limit resets on the 1st of every calendar month",
  quarterly: "Limit resets on the 1st of January, April, July, and October",
  weekly: "Limit resets every Sunday at midnight UTC",
};

export const refreshIntervalOptions = [
  ...refreshIntervals.map((int) => ({
    description: refreshIntervalDescriptions[int],
    label: capitalize(int),
    value: int,
  })),
];

export const budgetRefreshIntervalOptions = refreshIntervalOptions.filter(
  ({ value: v }) => budgetRefreshIntervals.includes(v as any),
);

export const refreshIntervalOptionsWithNone = [
  {
    label: "None",
    value: "null",
  },
  ...refreshIntervals.map((int) => ({
    label: int.charAt(0).toUpperCase() + int.slice(1),
    value: int,
  })),
];

export const allowedCategories = [
  "advertisingAndMarketing",
  "airlines",
  "carRental",
  "carService",
  "education",
  "electronics",
  "gasPump",
  "gasStation",
  "generalMerchandise",
  "government",
  "hardwareAndConstruction",
  "insurance",
  "legal",
  "lodging",
  "medical",
  "other",
  "parking",
  "phoneAndInternet",
  "professionalServices",
  "recreationAndEntertainment",
  "restaurants",
  "shipping",
  "software",
  "taxisAndRideshare",
  "transportation",
  "travelMisc",
  "utilities",
] as const;

export type CategoryCode = (typeof allowedCategories)[number];

// Map from category codes to formatted display names
export const categoryDisplayNames: Record<CategoryCode, string> = {
  advertisingAndMarketing: "Advertising & Marketing",
  airlines: "Airlines",
  carRental: "Car Rental",
  carService: "Car Service",
  education: "Education",
  electronics: "Electronics",
  gasPump: "Gas Pump",
  gasStation: "Gas Station",
  generalMerchandise: "General Merchandise",
  government: "Government",
  hardwareAndConstruction: "Hardware & Construction",
  insurance: "Insurance",
  legal: "Legal",
  lodging: "Lodging",
  medical: "Medical",
  other: "Other",
  parking: "Parking",
  phoneAndInternet: "Phone & Internet",
  professionalServices: "Professional Services",
  recreationAndEntertainment: "Recreation & Entertainment",
  restaurants: "Restaurants",
  shipping: "Shipping",
  software: "Software",
  taxisAndRideshare: "Taxis & Rideshare",
  transportation: "Transportation",
  travelMisc: "Travel Miscellaneous",
  utilities: "Utilities",
};

export const categoryOptions = allowedCategories.map((category) => ({
  label: categoryDisplayNames[category],
  value: category,
}));

export const stateCodes = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const;

export type State = (typeof stateCodes)[number];

export const salesTaxStates: State[] = ["UT", "NC"] as const;

// Object mapping state codes to full state names
export const stateNames: Record<State, string> = {
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
};

export const stateOptions = stateCodes.map((code) => ({
  label: stateNames[code],
  value: code,
}));

export type MemberRole = (typeof memberRoles)[number];
export const memberRoles = [
  "owner",
  "admin",
  "bookkeeper",
  "viewOnlyAdmin",
  "member",
  "none", // Member was removed from the organization
] as const;

export type NcCounty = keyof typeof ncCountyTaxRates;

// State sales tax rate is 4.75%
// North Carolina county tax rates
export const ncCountyTaxRates = {
  Alamance: 6.75,
  Alexander: 7,
  Alleghany: 7,
  Anson: 7,
  Ashe: 7,
  Avery: 6.75,
  Beaufort: 6.75,
  Bertie: 7,
  Bladen: 6.75,
  Brunswick: 6.75,
  Buncombe: 7,
  Burke: 6.75,
  Cabarrus: 7,
  Caldwell: 6.75,
  Camden: 6.75,
  Carteret: 6.75,
  Caswell: 6.75,
  Catawba: 7,
  Chatham: 7,
  Cherokee: 7,
  Chowan: 6.75,
  Clay: 7,
  Cleveland: 6.75,
  Columbus: 6.75,
  Craven: 6.75,
  Cumberland: 7,
  Currituck: 6.75,
  Dare: 6.75,
  Davidson: 7,
  Davie: 6.75,
  Duplin: 7,
  Durham: 7.5,
  Edgecombe: 7,
  Forsyth: 7,
  Franklin: 6.75,
  Gaston: 7,
  Gates: 6.75,
  Graham: 7,
  Granville: 6.75,
  Greene: 7,
  Guilford: 6.75,
  Halifax: 7,
  Harnett: 7,
  Haywood: 7,
  Henderson: 6.75,
  Hertford: 7,
  Hoke: 6.75,
  Hyde: 6.75,
  Iredell: 6.75,
  Jackson: 7,
  Johnston: 6.75,
  Jones: 7,
  Lee: 7,
  Lenoir: 6.75,
  Lincoln: 7,
  Macon: 6.75,
  Madison: 7,
  Martin: 7,
  McDowell: 6.75,
  Mecklenburg: 7.25,
  Mitchell: 6.75,
  Montgomery: 7,
  Moore: 7,
  Nash: 6.75,
  "New Hanover": 7,
  Northampton: 6.75,
  Onslow: 7,
  Orange: 7.5,
  Pamlico: 6.75,
  Pasquotank: 7,
  Pender: 6.75,
  Perquimans: 6.75,
  Person: 6.75,
  Pitt: 7,
  Polk: 6.75,
  Randolph: 7,
  Richmond: 6.75,
  Robeson: 7,
  Rockingham: 7,
  Rowan: 7,
  Rutherford: 7,
  Sampson: 7,
  Scotland: 6.75,
  Stanly: 7,
  Stokes: 6.75,
  Surry: 7,
  Swain: 7,
  Transylvania: 6.75,
  Tyrrell: 6.75,
  Union: 6.75,
  Vance: 6.75,
  Wake: 7.25,
  Warren: 6.75,
  Washington: 7,
  Watauga: 6.75,
  Wayne: 6.75,
  Wilkes: 7,
  Wilson: 6.75,
  Yadkin: 6.75,
  Yancey: 6.75,
} as const;

export const ncCountyCodes = {
  Alamance: 1,
  Alexander: 2,
  Alleghany: 3,
  Anson: 4,
  Ashe: 5,
  Avery: 6,
  Beaufort: 7,
  Bertie: 8,
  Bladen: 9,
  Brunswick: 10,
  Buncombe: 11,
  Burke: 12,
  Cabarrus: 13,
  Caldwell: 14,
  Camden: 15,
  Carteret: 16,
  Caswell: 17,
  Catawba: 18,
  Chatham: 19,
  Cherokee: 20,
  Chowan: 21,
  Clay: 22,
  Cleveland: 23,
  Columbus: 24,
  Craven: 25,
  Cumberland: 26,
  Currituck: 27,
  Dare: 28,
  Davidson: 29,
  Davie: 30,
  Duplin: 31,
  Durham: 32,
  Edgecombe: 33,
  Forsyth: 34,
  Franklin: 35,
  Gaston: 36,
  Gates: 37,
  Graham: 38,
  Granville: 39,
  Greene: 40,
  Guilford: 41,
  Halifax: 42,
  Harnett: 43,
  Haywood: 44,
  Henderson: 45,
  Hertford: 46,
  Hoke: 47,
  Hyde: 48,
  Iredell: 49,
  Jackson: 50,
  Johnston: 51,
  Jones: 52,
  Lee: 53,
  Lenoir: 54,
  Lincoln: 55,
  Macon: 56,
  Madison: 57,
  Martin: 58,
  McDowell: 59,
  Mecklenburg: 60,
  Mitchell: 61,
  Montgomery: 62,
  Moore: 63,
  Nash: 64,
  "New Hanover": 65,
  Northampton: 66,
  Onslow: 67,
  Orange: 68,
  Pamlico: 69,
  Pasquotank: 70,
  Pender: 71,
  Perquimans: 72,
  Person: 73,
  Pitt: 74,
  Polk: 75,
  Randolph: 76,
  Richmond: 77,
  Robeson: 78,
  Rockingham: 79,
  Rowan: 80,
  Rutherford: 81,
  Sampson: 82,
  Scotland: 83,
  Stanly: 84,
  Stokes: 85,
  Surry: 86,
  Swain: 87,
  Transylvania: 88,
  Tyrrell: 89,
  Union: 90,
  Vance: 91,
  Wake: 92,
  Warren: 93,
  Washington: 94,
  Watauga: 95,
  Wayne: 96,
  Wilkes: 97,
  Wilson: 98,
  Yadkin: 99,
  Yancey: 100,
} as const;

export const ncCounties = Object.keys(ncCountyTaxRates) as NcCounty[];

export const accountingCommonNames = {
  quickbooks: "QuickBooks",
  "quickbooks-desktop": "QuickBooks Desktop",
  xero: "Xero",
} as const;

export const offlineAccountingPlatforms = ["quickbooks-desktop"] as const;

export function isOfflinePlatform(
  v: unknown,
): v is (typeof offlineAccountingPlatforms)[number] {
  return offlineAccountingPlatforms.includes(v as any);
}

export function getAccountingViewUrl({
  platform,
  transactionPlatformId,
  accountPlatformId,
  sandbox,
}: {
  platform: AccountingPlatform | undefined;
  transactionPlatformId?: string | null;
  accountPlatformId?: string;
  sandbox: boolean;
}) {
  if (!transactionPlatformId || !platform) {
    return null;
  }

  if (platform === "quickbooks") {
    return sandbox
      ? `https://sandbox.qbo.intuit.com/app/expense?txnId=${transactionPlatformId}`
      : `https://qbo.intuit.com/app/expense?txnId=${transactionPlatformId}`;
  } else if (platform === "xero" && accountPlatformId) {
    return `https://go.xero.com/Bank/ViewTransaction.aspx?bankTransactionID=${transactionPlatformId}&accountID=${accountPlatformId}`;
  }

  return null;
}

export const cardDefaults = {
  addressId: null as string | null,
  allowedBudgets: [] as string[],
  approvalPolicyId: null as string | null,
  budgetRestricted: true,
  defaultBudgetId: null as string | null,
  description: "" as string | null,
  forUserId: null as string | null,
  name: "",
  policyId: null as string | null,
  restrictions: {
    allowedCategories: [] as any[],
    periodLimitCents: 0,
    periodTargetCents: 0,
    refreshInterval: "daily" as any,
    schedule: {
      activateAt: null as Date | null,
      lockAt: null as Date | null,
    },
  },
  saveNewAddress: true,
  type: null as string | null,
};

export const organizationSizeOptions = [
  { label: "Just me", value: "1" },
  { label: "2-5 staff", value: "2-5" },
  { label: "6-10 staff", value: "6-10" },
  { label: "11-20 staff", value: "11-20" },
  { label: "21-30 staff", value: "20-30" },
  { label: "31-50 staff", value: "31-50" },
  { label: "51-100 staff", value: "51-100" },
  { label: "101-250 staff", value: "101-250" },
  { label: "251+ staff", value: "251+" },
] as const;

export type OrganizationSize =
  (typeof organizationSizeOptions)[number]["value"];

export const monthlySpendOptions = [
  { label: "Less than $1,000", value: "under-1k" },
  { label: "$1,000 - $2,500", value: "1-2.5k" },
  { label: "$2,500 - $5,000", value: "2.5-5k" },
  { label: "$5,000 - $10,000", value: "5-10k" },
  { label: "$10,000 - $20,000", value: "10k-20k" },
  { label: "$20,000 - $50,000", value: "20-50k" },
  { label: "$50,000 - $100,000", value: "50-100k" },
  { label: "Over $100,000", value: "over-100k" },
] as const;

export type MonthlySpend = (typeof monthlySpendOptions)[number]["value"];
export const CHECKBOOK_CHECK_STATUSES = [
  "UNPAID",
  "IN_PROCESS", // Processing state
  "FAILED",
  "VOID", // Voided check
  "EXPIRED", // Expired check
  "PRINTED", // Digital check printed (terminal state)
  "MAILED", // Physical check mailed (terminal state)
  "PAID", // Terminal state for ACH
  "REFUNDED",
] as const;

export type CheckbookCheckStatusType =
  (typeof CHECKBOOK_CHECK_STATUSES)[number];

export const hourMs = 60 * 60 * 1000;
export const dayMs = 24 * hourMs;

export const accountingFieldOrdering = {
  quickbooks: [
    "expense_account",
    "payment_account",
    "payee",
    "class",
    "location",
  ] as string[],
  "quickbooks-desktop": [
    "expense_account",
    "payment_account",
    "payee",
    "class",
  ],
  xero: ["payment_account", "expense_account", "contact"] as string[],
} as const;

export const emailPreferenceFrequencies = [
  "daily",
  "other-days",
  "weekly",
] as const;

export const accountingPlatforms = [
  "quickbooks",
  "quickbooks-desktop",
  "xero",
] as const;
export type AccountingPlatform = (typeof accountingPlatforms)[number];
